import { PageProps } from 'libs/contentful/queries/page/props';
import { isInternalLink, isEnrollmentUrl } from 'utils/linkHelpers';

interface TransformLinksOptions {
  searchParams?: URLSearchParams;
  subdomain?: string;
  enrollmentUrl?: string;
}

/**
 * Removes enterprise prefix from URLs when on enterprise subdomain
 * This replicates the behavior of both getRelativePath and rewriteEnterpriseSlug
 */
function removeEnterprisePrefix(url: string): string {
  // Only handle relative paths - absolute URLs should remain unchanged
  if (url.startsWith('/enterprise/')) {
    return url.substring('/enterprise'.length);
  }

  // Do NOT modify absolute URLs - they should keep their enterprise prefix
  return url;
}

/**
 * Recursively transforms all links in a page's data to include query parameters
 * This ensures links are complete when they reach components
 */
export function transformPageLinks<T extends PageProps>(page: T, options: TransformLinksOptions = {}): T {
  const { searchParams, subdomain } = options;

  if (!searchParams && subdomain !== 'enterprise') {
    return page;
  }

  // Transform links in all modules, following the same pattern as replaceAllDynamicTokens
  return transformAllUrls(page, searchParams, subdomain);
}

/**
 * Recursively transform all URLs in data, following the same pattern as replaceAllDynamicTokens
 */
export const transformAllUrls = <T extends PageProps>(
  data: T,
  searchParams?: URLSearchParams,
  subdomain?: string,
): T => {
  const processedObjects = new WeakMap<object, unknown>();

  const processValue = (value: unknown): unknown => {
    // Handle null/undefined
    if (value == null) {
      return value;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      const cachedArray = processedObjects.get(value);
      if (cachedArray) {
        return cachedArray;
      }

      const transformedArray: unknown[] = [];
      processedObjects.set(value, transformedArray);

      value.forEach((item, index) => {
        transformedArray[index] = processValue(item);
      });

      return transformedArray;
    }

    // Handle objects (recursively process all properties)
    if (typeof value === 'object') {
      const cachedObject = processedObjects.get(value);
      if (cachedObject) {
        return cachedObject;
      }

      // Handle Map instances
      if (value instanceof Map) {
        const clonedMap = new Map();
        processedObjects.set(value, clonedMap);

        value.forEach((val, key) => {
          clonedMap.set(key, processValue(val));
        });
        return clonedMap;
      }

      // Handle Date instances
      if (value instanceof Date) {
        return new Date(value.getTime());
      }

      let transformedValue: object = value;

      // If this object has URL or page reference properties, transform them first.
      if (hasTransformableUrl(transformedValue)) {
        const urlObject =
          isEnrollmentUrl(transformedValue.url) && data.enrollmentUrl
            ? { ...transformedValue, url: mergeQueryParams(data.enrollmentUrl, transformedValue.url) }
            : transformedValue;

        transformedValue = transformUrl(urlObject, searchParams, subdomain);
      }

      if (hasTransformablePageReference(transformedValue)) {
        transformedValue = transformPageReference(transformedValue, searchParams, subdomain);
      }

      // Handle plain objects
      const result: Record<string, unknown> = {};
      processedObjects.set(value, result);

      for (const [key, val] of Object.entries(transformedValue)) {
        result[key] = processValue(val);
      }
      return result;
    }

    // Return primitive values as-is
    return value;
  };

  return processValue(data) as T;
};

/**
 * Transform any object with a URL to include query parameters and handle enterprise prefix
 * Returns a new object with the transformed URL
 */
function transformUrl<T extends { url: string; [key: string]: unknown }>(
  obj: T,
  searchParams?: URLSearchParams,
  subdomain?: string,
): T {
  // First handle enterprise prefix removal if needed
  let processedUrl = obj.url;
  if (subdomain === 'enterprise' && isInternalLink(obj.url)) {
    processedUrl = removeEnterprisePrefix(obj.url);
  }

  // Then add query params if provided
  if (searchParams) {
    processedUrl = addQueryParamsToUrl(processedUrl, searchParams);
  }

  return {
    ...obj,
    url: processedUrl,
  };
}

/**
 * Merge query parameters from source URL into target URL
 * Preserves existing query parameters in target URL
 */
function mergeQueryParams(targetUrl: string, sourceUrl: string): string {
  try {
    // Parse both URLs
    const targetUrlObj = new URL(targetUrl);
    const sourceUrlObj = new URL(sourceUrl);

    // Merge query parameters from source into target
    sourceUrlObj.searchParams.forEach((value, key) => {
      targetUrlObj.searchParams.set(key, value);
    });

    return targetUrlObj.toString();
  } catch {
    // If URL parsing fails, return original target URL
    return targetUrl;
  }
}

/**
 * Add query parameters to a URL.
 * Skips external URLs and fragment-only links; merges params into internal and enrollment URLs.
 */
export function addQueryParamsToUrl(url: string, searchParams: URLSearchParams): string {
  // Skip external URLs (not chime.com or 1debit.com)
  // Only process internal links and relative paths
  if (!isInternalLink(url)) {
    return url;
  }

  // Skip fragment-only links
  if (url.startsWith('#')) {
    return url;
  }

  // For enrollment URLs, use special handling that preserves existing params
  if (isEnrollmentUrl(url)) {
    try {
      // Parse the enrollment URL
      const urlObj = new URL(url);

      // Merge searchParams into the URL
      // searchParams take priority and override existing params
      searchParams.forEach((value, key) => {
        urlObj.searchParams.set(key, value);
      });

      return urlObj.toString();
    } catch {
      // If URL parsing fails, return original
      return url;
    }
  }

  try {
    // Parse the URL
    const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, 'https://chime.com');

    // Add search params to the URL
    // Custom params from the CTA take priority and will override existing params
    searchParams.forEach((value, key) => {
      urlObj.searchParams.set(key, value);
    });

    // Return the appropriate format
    if (url.startsWith('http')) {
      return urlObj.toString();
    } else {
      return urlObj.pathname + urlObj.search + urlObj.hash;
    }
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Replaces a set of query param keys with new values: removes those keys from the URL first, then sets the new params.
 * Use when switching between variant QSP sets so old params (e.g. from previous variant) are removed.
 */
export function replaceQueryParamsInUrl(
  url: string,
  keysToReplace: ReadonlySet<string>,
  newSearchParams: URLSearchParams,
): string {
  if (!isInternalLink(url) || url.startsWith('#')) {
    return url;
  }
  try {
    const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, 'https://chime.com');
    keysToReplace.forEach(key => urlObj.searchParams.delete(key));
    newSearchParams.forEach((value, key) => urlObj.searchParams.set(key, value));
    if (url.startsWith('http')) {
      return urlObj.toString();
    }
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    return url;
  }
}

/**
 * Type representing an object with a URL property
 */
type UrlObject = { url: string } & Record<string, unknown>;

/**
 * Type representing an object with page reference properties
 */
type PageRefObject = {
  internalPage?: { slug: string };
  resourcePage?: { slug: string };
  resourcesInternalPage?: { slug: string };
} & Record<string, unknown>;

/**
 * Type guard to check if an object has a transformable URL
 */
function hasTransformableUrl(obj: unknown): obj is UrlObject {
  if (obj === null || typeof obj !== 'object') return false;

  const objWithUrl = obj as Record<string, unknown>;
  return 'url' in objWithUrl && typeof objWithUrl.url === 'string';
}

/**
 * Type guard to check if an object has a transformable page reference
 */
function hasTransformablePageReference(obj: unknown): obj is PageRefObject {
  if (obj === null || typeof obj !== 'object') return false;

  const objWithPage = obj as Record<string, unknown>;

  // List of properties that contain InternalPage references
  const pageProperties = ['internalPage', 'resourcePage', 'resourcesInternalPage'];

  // Check if any of these properties exist and are valid
  return pageProperties.some(prop => {
    const pageRef = objWithPage[prop];
    return (
      pageRef !== null &&
      typeof pageRef === 'object' &&
      'slug' in (pageRef as Record<string, unknown>) &&
      typeof (pageRef as Record<string, unknown>).slug === 'string'
    );
  });
}

/**
 * Transform any object with page references to handle enterprise prefix and query parameters
 * Returns a new object with the transformed page reference
 */
function transformPageReference<
  T extends {
    internalPage?: { slug: string };
    resourcePage?: { slug: string };
    resourcesInternalPage?: { slug: string };
    [key: string]: unknown;
  },
>(obj: T, searchParams?: URLSearchParams, subdomain?: string): T {
  // List of properties that contain InternalPage references
  const pageProperties = ['internalPage', 'resourcePage', 'resourcesInternalPage'] as const;

  // Find which property exists in this object
  const pageProperty = pageProperties.find(prop => obj[prop as keyof T]);
  if (!pageProperty) return obj;

  const pageObj = obj[pageProperty as keyof T] as { slug: string };
  if (!pageObj) return obj;

  let processedSlug = pageObj.slug;

  // Handle enterprise prefix removal if needed
  if (subdomain === 'enterprise' && processedSlug.startsWith('enterprise/')) {
    processedSlug = processedSlug.substring('enterprise/'.length);
  }

  // Add query params if provided
  if (searchParams && searchParams.toString()) {
    const separator = processedSlug.includes('?') ? '&' : '?';
    processedSlug = processedSlug + separator + searchParams.toString();
  }

  return {
    ...obj,
    [pageProperty]: {
      ...pageObj,
      slug: processedSlug,
    },
  };
}
